import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useParams } from "react-router-dom";
import { editCollection, viewOneCollection } from "../../Redux/Actions/collectionsResaleActions";
import { useDispatch, useSelector } from "react-redux";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";
import "./editSubCollections.css";

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const UploadField = ({ label, fieldKey, value, onUpload, onRemove, loading, required }) => (
  <div className="esc-field">
    <label className="esc-label">
      {label}
      {required ? <span className="esc-req"> *</span> : <span className="esc-optional"> (Optional)</span>}
    </label>
    {value ? (
      <div className="esc-image-preview">
        <img src={value} alt={label} className="esc-preview-img" />
        <div className="esc-preview-info">
          <span className="esc-preview-label">Uploaded</span>
          <button type="button" className="esc-remove-btn" onClick={() => onRemove(fieldKey)} disabled={loading}>
            <TrashIcon />
            Remove
          </button>
        </div>
      </div>
    ) : (
      <label className="esc-upload-zone" htmlFor={`esc-upload-${fieldKey}`}>
        <input
          id={`esc-upload-${fieldKey}`}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onUpload(e, fieldKey)}
          disabled={loading}
        />
        <div className="esc-upload-icon"><UploadIcon /></div>
        <span className="esc-upload-text">Click to upload</span>
        <span className="esc-upload-hint">PNG, JPG, SVG up to 2MB</span>
      </label>
    )}
  </div>
);

const EditSubCollections = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const [subCollectionState, setSubCollectionState] = useState({
    id: id,
    name: "",
    image: "",
    icon: "",
    collection_id: "",
    banner_image: "",
    banner_title: "",
    three_d_icon: "",
  });

  useEffect(() => {
    dispatch(viewOneCollection(id));
  }, [id]);

  const { collectionResaleDetails } = useSelector((state) => state.collectionsResale || {});

  useEffect(() => {
    if (collectionResaleDetails?.data) {
      setSubCollectionState((prev) => ({
        ...prev,
        ...collectionResaleDetails.data,
        id: id,
      }));
    }
  }, [collectionResaleDetails, id]);

  const handleFileChange = async (event, type) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    const responseUrl = await AddImage(uploadedFile);
    if (responseUrl) {
      setSubCollectionState((prev) => ({ ...prev, [type]: responseUrl }));
    }
  };

  const handleRemove = (fieldKey) => {
    setSubCollectionState((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleNextSection = () => {
    if (activeSection === 0 && !subCollectionState.name.trim()) {
      toast.error("Sub-collection name is required");
      return;
    }
    setActiveSection((s) => Math.min(sections.length - 1, s + 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subCollectionState.name.trim()) return toast.error("Sub-collection name is required");

    setLoading(true);
    try {
      const filteredData = Object.fromEntries(
        Object.entries(subCollectionState).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      );
      const response = await dispatch(editCollection(filteredData));
      if (response?.statusCode === 200) {
        toast.success("Sub-collection updated successfully!");
        navigate(-1);
      } else {
        toast.error(response?.message || "Failed to update sub-collection");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const sections = ["Basic Info", "Media"];

  return (
    <MainLayout>
      <div className="esc-wrapper">
        <div className="esc-header">
          <div className="esc-header-left">
            <div className="esc-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <div>
              <h1 className="esc-title">Edit Sub Collection</h1>
              <p className="esc-subtitle">Update the details of this sub-collection</p>
            </div>
          </div>
          <button type="button" className="esc-view-btn" onClick={() => navigate(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Go Back
          </button>
        </div>

        <div className="esc-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`esc-nav-pill ${activeSection === i ? "esc-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading}
            >
              <span className="esc-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <div className="esc-form">
          <div className={`esc-section ${activeSection === 0 ? "esc-section--visible" : ""}`}>
            <div className="esc-section-header">
              <span className="esc-section-badge">01</span>
              <h2 className="esc-section-title">Basic Information</h2>
            </div>
            <div className="esc-grid esc-grid-2">
              <div className="esc-field">
                <label className="esc-label">Sub Collection Name <span className="esc-req">*</span></label>
                <input
                  className="esc-input"
                  type="text"
                  value={subCollectionState.name}
                  onChange={(e) => setSubCollectionState((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter Name"
                  disabled={loading}
                />
              </div>
              <div className="esc-field">
                <label className="esc-label">Banner Title <span className="esc-optional">(Optional)</span></label>
                <input
                  className="esc-input"
                  type="text"
                  value={subCollectionState.banner_title}
                  onChange={(e) => setSubCollectionState((prev) => ({ ...prev, banner_title: e.target.value }))}
                  placeholder="Enter Title"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className={`esc-section ${activeSection === 1 ? "esc-section--visible" : ""}`}>
            <div className="esc-section-header">
              <span className="esc-section-badge">02</span>
              <h2 className="esc-section-title">Media</h2>
            </div>
            <div className="esc-grid esc-grid-2">
              <UploadField
                label="Sub-Collection Image"
                fieldKey="image"
                value={subCollectionState.image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Sub-Collection Icon"
                fieldKey="icon"
                value={subCollectionState.icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Banner Image"
                fieldKey="banner_image"
                value={subCollectionState.banner_image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="3D Icon"
                fieldKey="three_d_icon"
                value={subCollectionState.three_d_icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
            </div>
          </div>

          <div className="esc-footer">
            <div className="esc-footer-nav">
              <button
                type="button"
                className="esc-btn esc-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button type="button" className="esc-btn esc-btn--primary" onClick={handleNextSection} disabled={loading}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="esc-btn esc-btn--submit" disabled={loading}>
                  {loading ? "Saving..." : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Update Sub Collection
                    </>
                  )}
                </button>
              )}
            </div>
            <button type="button" className="esc-btn esc-btn--cancel" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditSubCollections;